import React, { useState, useMemo } from 'react';
import { format, eachDayOfInterval, parseISO, isSameMonth, startOfMonth, endOfMonth, addMonths, subMonths, getDay, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronLeft, ChevronRight, Clock, Plus, X, Calendar, Check } from 'lucide-react';

const CalendarioProgrammazione = ({ 
  dataInizio, 
  dataFine, 
  programmazione = [], 
  onProgrammazioneChange 
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (dataInizio) {
      return startOfMonth(parseISO(dataInizio));
    }
    return startOfMonth(new Date());
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newOrario, setNewOrario] = useState('');

  // Giorni della campagna
  const campaignDays = useMemo(() => {
    if (!dataInizio || !dataFine) return [];
    try {
      return eachDayOfInterval({ 
        start: parseISO(dataInizio), 
        end: parseISO(dataFine) 
      });
    } catch { 
      return []; 
    }
  }, [dataInizio, dataFine]);

  // Verifica se un giorno è nella campagna
  const isDayCampaign = (day) => {
    return campaignDays.some(cd => isSameDay(cd, day));
  };

  // Ottieni programmazione per un giorno specifico
  const getDayProgrammazione = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return programmazione.find(p => p.data === dateStr);
  };

  // Giorni del mese per il calendario
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Aggiungi padding per iniziare dalla domenica/lunedì corretto
    const startDay = getDay(start);
    const paddingDays = startDay === 0 ? 6 : startDay - 1; // Lunedì = 0
    
    const paddedDays = [];
    for (let i = paddingDays; i > 0; i--) {
      const d = new Date(start);
      d.setDate(d.getDate() - i);
      paddedDays.push({ date: d, isCurrentMonth: false });
    }
    
    days.forEach(d => paddedDays.push({ date: d, isCurrentMonth: true }));
    
    // Aggiungi giorni alla fine per completare la griglia
    const remaining = 42 - paddedDays.length; // 6 righe x 7 colonne
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(end);
      d.setDate(d.getDate() + i);
      paddedDays.push({ date: d, isCurrentMonth: false });
    }
    
    return paddedDays;
  }, [currentMonth]);

  // Statistiche
  const stats = useMemo(() => {
    const giorniProgrammati = programmazione.filter(p => p.orari && p.orari.length > 0).length;
    const totalePassaggi = programmazione.reduce((sum, p) => sum + (p.orari?.length || 0), 0);
    return { giorniProgrammati, totalePassaggi, totaleGiorni: campaignDays.length };
  }, [programmazione, campaignDays]);

  const handleDayClick = (day) => {
    if (isDayCampaign(day)) {
      setSelectedDay(day);
      setDialogOpen(true);
      setNewOrario('');
    }
  };

  const handleAddOrario = () => {
    if (!newOrario || !selectedDay) return;
    
    const dateStr = format(selectedDay, 'yyyy-MM-dd');
    const existing = programmazione.find(p => p.data === dateStr);
    const orari = existing?.orari || [];
    
    // Verifica formato orario (HH:MM)
    const orarioRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (!orarioRegex.test(newOrario)) {
      return;
    }
    
    if (!orari.includes(newOrario)) {
      const newOrari = [...orari, newOrario].sort();
      updateProgrammazione(dateStr, newOrari);
    }
    setNewOrario('');
  };

  const handleRemoveOrario = (dateStr, orario) => {
    const existing = programmazione.find(p => p.data === dateStr);
    if (existing) {
      const newOrari = existing.orari.filter(o => o !== orario);
      updateProgrammazione(dateStr, newOrari);
    }
  };

  const updateProgrammazione = (dateStr, orari) => {
    const newProg = programmazione.filter(p => p.data !== dateStr);
    if (orari.length > 0) {
      newProg.push({ data: dateStr, orari });
    }
    onProgrammazioneChange(newProg);
  };

  const handleQuickAdd = (orario) => {
    if (!selectedDay) return;
    const dateStr = format(selectedDay, 'yyyy-MM-dd');
    const existing = programmazione.find(p => p.data === dateStr);
    const orari = existing?.orari || [];
    
    if (!orari.includes(orario)) {
      const newOrari = [...orari, orario].sort();
      updateProgrammazione(dateStr, newOrari);
    }
  };

  const selectedDayProg = selectedDay ? getDayProgrammazione(selectedDay) : null;

  // Fasce orarie comuni per quick-add
  const fasceOrarie = [
    { label: 'Mattina', orari: ['07:00', '08:00', '09:00', '10:00', '11:00'] },
    { label: 'Pranzo', orari: ['12:00', '13:00', '14:00'] },
    { label: 'Pomeriggio', orari: ['15:00', '16:00', '17:00', '18:00'] },
    { label: 'Sera', orari: ['19:00', '20:00', '21:00', '22:00'] }
  ];

  if (!dataInizio || !dataFine) {
    return (
      <Card data-testid="calendario-programmazione-card">
        <CardHeader>
          <CardTitle className="font-['Manrope'] flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            Calendario Messa in Onda
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            <Calendar size={40} className="mx-auto mb-3 text-slate-300" />
            <p>Imposta le date di inizio e fine</p>
            <p className="text-sm">per visualizzare il calendario</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="calendario-programmazione-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-['Manrope'] flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            Calendario Messa in Onda
          </CardTitle>
          <CardDescription>
            Clicca su un giorno per programmare gli orari di trasmissione
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-slate-50 rounded-lg">
              <p className="text-lg font-bold text-primary">{stats.totaleGiorni}</p>
              <p className="text-xs text-slate-500">Giorni totali</p>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <p className="text-lg font-bold text-green-600">{stats.giorniProgrammati}</p>
              <p className="text-xs text-slate-500">Programmati</p>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <p className="text-lg font-bold text-amber-600">{stats.totalePassaggi}</p>
              <p className="text-xs text-slate-500">Tot. passaggi</p>
            </div>
          </div>

          {/* Calendar Navigation */}
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              data-testid="calendar-prev-month"
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="font-semibold text-sm">
              {format(currentMonth, 'MMMM yyyy', { locale: it })}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              data-testid="calendar-next-month"
            >
              <ChevronRight size={16} />
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="rounded-lg border overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-7 bg-slate-100">
              {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
                <div key={day} className="py-2 text-center text-xs font-medium text-slate-600">
                  {day}
                </div>
              ))}
            </div>
            {/* Days */}
            <div className="grid grid-cols-7">
              {calendarDays.map(({ date, isCurrentMonth }, idx) => {
                const isCampaign = isDayCampaign(date);
                const dayProg = getDayProgrammazione(date);
                const hasOrari = dayProg?.orari?.length > 0;
                
                return (
                  <button
                    key={idx}
                    onClick={() => handleDayClick(date)}
                    disabled={!isCampaign}
                    className={`
                      relative aspect-square p-1 text-sm border-r border-b transition-all
                      ${!isCurrentMonth ? 'text-slate-300 bg-slate-50' : ''}
                      ${isCampaign ? 'cursor-pointer hover:bg-primary/10 bg-primary/5' : 'cursor-default'}
                      ${hasOrari ? 'bg-green-100 hover:bg-green-200' : ''}
                    `}
                    data-testid={isCampaign ? `calendar-day-${format(date, 'yyyy-MM-dd')}` : undefined}
                  >
                    <span className={`
                      ${isCampaign ? 'font-semibold text-primary' : ''}
                      ${hasOrari ? 'text-green-700' : ''}
                    `}>
                      {format(date, 'd')}
                    </span>
                    {hasOrari && (
                      <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2">
                        <div className="flex gap-0.5">
                          {dayProg.orari.slice(0, 3).map((_, i) => (
                            <div key={i} className="w-1 h-1 rounded-full bg-green-600" />
                          ))}
                          {dayProg.orari.length > 3 && (
                            <span className="text-[8px] text-green-600">+{dayProg.orari.length - 3}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex gap-4 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-primary/10 border border-primary/30" />
              <span>Giorno campagna</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-100 border border-green-300" />
              <span>Con orari</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog per modificare orari */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {selectedDay && format(selectedDay, 'EEEE d MMMM yyyy', { locale: it })}
            </DialogTitle>
            <DialogDescription>
              Gestisci gli orari di messa in onda per questo giorno
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Orari attuali */}
            <div>
              <p className="text-sm font-medium mb-2">Orari programmati</p>
              {selectedDayProg?.orari?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedDayProg.orari.map((orario) => (
                    <Badge 
                      key={orario} 
                      variant="secondary"
                      className="flex items-center gap-1 px-3 py-1"
                    >
                      <Clock size={12} />
                      {orario}
                      <button
                        onClick={() => handleRemoveOrario(format(selectedDay, 'yyyy-MM-dd'), orario)}
                        className="ml-1 hover:text-red-500"
                        data-testid={`remove-orario-${orario}`}
                      >
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Nessun orario programmato</p>
              )}
            </div>

            {/* Aggiungi orario manualmente */}
            <div>
              <p className="text-sm font-medium mb-2">Aggiungi orario</p>
              <div className="flex gap-2">
                <Input
                  type="time"
                  value={newOrario}
                  onChange={(e) => setNewOrario(e.target.value)}
                  className="flex-1"
                  data-testid="input-new-orario"
                />
                <Button onClick={handleAddOrario} size="icon" data-testid="btn-add-orario">
                  <Plus size={18} />
                </Button>
              </div>
            </div>

            {/* Quick add fasce orarie */}
            <div>
              <p className="text-sm font-medium mb-2">Aggiungi rapido</p>
              <ScrollArea className="h-[200px] pr-4">
                <div className="space-y-3">
                  {fasceOrarie.map((fascia) => (
                    <div key={fascia.label}>
                      <p className="text-xs text-slate-500 mb-1">{fascia.label}</p>
                      <div className="flex flex-wrap gap-1">
                        {fascia.orari.map((orario) => {
                          const isSelected = selectedDayProg?.orari?.includes(orario);
                          return (
                            <Button
                              key={orario}
                              variant={isSelected ? 'default' : 'outline'}
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => isSelected 
                                ? handleRemoveOrario(format(selectedDay, 'yyyy-MM-dd'), orario)
                                : handleQuickAdd(orario)
                              }
                              data-testid={`quick-add-${orario}`}
                            >
                              {isSelected && <Check size={12} className="mr-1" />}
                              {orario}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setDialogOpen(false)} data-testid="close-dialog-btn">
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CalendarioProgrammazione;
